import { AlertData } from '../../../interfaces'
import { alerts } from '../../../utils/constants'
import { useDispatch, useSelector } from 'react-redux'
import { sendVitalAlertNotification } from '../../../utils/services'
import { MouseEvent, ReactElement, useEffect, useState } from 'react'
import {
  titleCase,
  StyledMenu,
  findExistingAlertIndex,
} from '../../../utils/utils'
import {
  setPatientUHID,
  setCurrentLocation,
  setSelectedPatientIndex,
} from '../../../redux/actions'
import useSound from 'use-sound'
import Box from '@mui/material/Box'
import AlertItem from './AlertItem'
import Badge from '@mui/material/Badge'
import Button from '@mui/material/Button'
import NotificationsIcon from '@mui/icons-material/Notifications'
import useVitalAlertSocket from '../../../utils/useVitalAlertSocket'
import useGetPatientDetails from '../../../utils/useGetPatientDetails'
import './VitalAlerts.css'

const VitalAlerts = (): ReactElement => {
  const alertSound = require('../../../assets/AlertSound/alertSound.mp3')
  const [play] = useSound(alertSound, { volume: 0.2 })
  const { fetchPatientDetails } = useGetPatientDetails()
  const patientDetails = useSelector((state: any) => ({
    masterPatientDetails: state.masterPatientDetails,
    patient_bed_number: state.patient_bed_number,
    patient_room_number: state.patient_room_number,
    patient_ward_name: state.patient_ward_name,
  }))

  const [isInitialRender, setIsInitialRender] = useState<boolean>(true)
  const [alertData, setAlertData] = useState<AlertData[]>([])
  const [alertClicked, setAlertClicked] = useState(
    alertData?.filter((alert) => !alert?.isClicked)?.length
  )
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const dispatch = useDispatch()
  const open = Boolean(anchorEl)

  const handleIncomingVitalAlerts = (message: any) => {
    if (!message?.alarm_data) return

    const formattedAlertData = {
      muhid: message?.patient_id,
      location: patientDetails?.masterPatientDetails
        ?.flat()
        ?.find((item) => item?.patientId === message?.patient_id)?.location,
      vitals: message?.alarm_data?.[1]?.alarm_type,
      isClicked: false,
      count: 1,
      value: message?.alarm_data?.[1]?.alarm_value,
      value_limit: message?.alarm_data?.[0]?.alarm_value,
    }

    setAlertData((prevAlertData) =>
      handleUpdateAlertData(prevAlertData, formattedAlertData)
    )

    const boolCheck =
      process.env.REACT_APP_EXECUTE_DOCTOR_APP_NOTIFICATION === 'true'

    if (boolCheck) {
      sendVitalAlertNotification(
        formattedAlertData?.muhid ?? '',
        formattedAlertData?.vitals ?? '',
        formattedAlertData?.value ?? '',
        formattedAlertData?.value_limit ?? '',
        message?.alarm_data?.[2]?.alarm_value ?? '',
        titleCase(patientDetails?.patient_bed_number),
        titleCase(patientDetails?.patient_room_number),
        titleCase(patientDetails?.patient_ward_name)
      )
    }
  }

  const {
    createVitalAlertSocket,
    sendMessageOnSocket,
    closeProrithmWebSocket,
  } = useVitalAlertSocket({ handleIncomingVitalAlerts })

  const handleAlertBtnClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event?.currentTarget)
  }

  const handleUpdateAlertData = (prevAlertData, formattedAlertData) => {
    const existingAlertIndex = findExistingAlertIndex(
      prevAlertData,
      formattedAlertData
    )

    if (existingAlertIndex !== -1) {
      const updatedAlertData = [...prevAlertData]
      const count = updatedAlertData?.[existingAlertIndex]?.count
      updatedAlertData[existingAlertIndex] = formattedAlertData
      updatedAlertData[existingAlertIndex].count = count + 1
      return updatedAlertData
    } else {
      return [...prevAlertData, formattedAlertData]
    }
  }

  const handleAlertItemClick = (
    index: number,
    muhid: string,
    location: string
  ) => {
    const patientsList = patientDetails?.masterPatientDetails?.find(
      (subArray) => subArray?.some((patient) => patient?.patientId === muhid)
    )
    const updatedAlertData = alertData?.map((alert, i) => {
      if (i === index) {
        return { ...alert, isClicked: true }
      }
      return alert
    })

    setAlertData(updatedAlertData)
    setAnchorEl(null)
    if (
      patientDetails?.masterPatientDetails
        ?.flat()
        ?.some((patient) => patient?.patientId === muhid)
    ) {
      fetchPatientDetails(muhid)
      dispatch(setPatientUHID(muhid))
      dispatch(setCurrentLocation(location))
      const patientIndex = patientsList?.findIndex(
        (item) => item?.patientId === muhid
      )
      dispatch(setSelectedPatientIndex(patientIndex))
    } else {
      alert(alerts?.alertsError)
    }
  }

  const removeAlert = (
    e: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    e.stopPropagation()
    const updatedAlertData = alertData?.filter((_, i) => i !== index)
    setAlertData(updatedAlertData)
  }

  useEffect(() => {
    setAlertClicked(alertData?.filter((alert) => !alert?.isClicked)?.length)
  }, [alertData])

  useEffect(() => {
    createVitalAlertSocket()
    const dummyMessageInterval = setInterval(() => {
      sendMessageOnSocket()
    }, Number(process.env.REACT_APP_THOP_SOCKET_DUMMY_MESSAGE_DELAY))

    return () => {
      closeProrithmWebSocket()
      clearInterval(dummyMessageInterval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false)
      return
    }
    play()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertData, isInitialRender])

  return (
    <Box display='flex' justifyContent='center' paddingX='20px' marginY={1}>
      {alertData?.length > 0 ? (
        <div id='alerts-appeared'>
          <Button
            disableElevation
            variant='contained'
            aria-haspopup='true'
            id='vitals-alert-btn'
            className='alert-button'
            aria-label='vitals alert'
            onClick={handleAlertBtnClick}
            data-testid='vitals-alert-button'
            aria-expanded={open ? 'true' : undefined}
            aria-controls={open ? 'alerts-menu' : undefined}
          >
            <Badge
              color='error'
              badgeContent={alertClicked}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <NotificationsIcon fontSize='medium' />
            </Badge>
          </Button>
          <StyledMenu
            open={open}
            id='alerts-menu'
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            MenuListProps={{
              'aria-labelledby': 'vitals-alert-button',
            }}
          >
            {alertData?.map((item: AlertData, index: number) => {
              return (
                <AlertItem
                  key={crypto.randomUUID()}
                  item={item}
                  index={index}
                  removeAlert={removeAlert}
                  handleAlertItemClick={handleAlertItemClick}
                />
              )
            })}
          </StyledMenu>
        </div>
      ) : null}
    </Box>
  )
}

export default VitalAlerts
